import type {
  Cell,
  Column,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnVisibilityState,
  Header,
  Row,
  RowData,
  SortingState,
} from '@tanstack/react-table';
import { cn } from 'cn';
import { TableBody } from './private/TableBody';
import { TableFooter } from './private/TableFooter';
import { TableHeader } from './private/TableHeader';
import { TablePagination } from './private/TablePagination';
import { useTableContainer } from './private/useTableContainer';
import { useTableInstance } from './private/useTableInstance';
import type { kkTableFeatures, TableColumnDef } from './tableFeatures';

export type TableVariant = 'default' | 'relaxed' | 'compact' | 'list';

export interface TableRowSelection<TData extends RowData> {
  /** ID of the currently selected row. Use null when no row is selected. */
  selectedRowId: string | null;
  /** Returns the stable business ID used to preserve selection across table state changes. */
  getRowId: (originalRow: TData, index: number) => string;
  /** Called when a different row is selected. */
  onChange: (row: Row<typeof kkTableFeatures, TData>) => void;
  /** Shows a check-circle before the first cell's content in the selected row. */
  showSelectionIndicator?: boolean;
}

type SharedTableProps<TData extends RowData, TDisplayKeys extends string> = {
  className?: string;
  columns: TableColumnDef<TData>[];
  data: TData[];
  fixed?: boolean;
  paginated?: boolean;
  stickyHeader?: boolean;
  pinning?: {
    left: Array<keyof TData | TDisplayKeys>;
    right: Array<keyof TData | TDisplayKeys>;
  };
  pageSize?: number;
  pageCount?: number;
  sortable?: boolean;
  sortDescFirst?: boolean;
  filterable?: boolean;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  filters?: ColumnFiltersState;
  manualPagination?: boolean;
  getRowClassName?: (row: Row<typeof kkTableFeatures, TData>) => string;
  getCellClassName?: (
    cell: Cell<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  getHeaderClassName?: (
    cell: Header<typeof kkTableFeatures, TData, unknown>,
  ) => string;
  caption?: string;
  onRowClick?: (row: Row<typeof kkTableFeatures, TData>) => void;
  onRowMouseEnter?: (row: Row<typeof kkTableFeatures, TData>) => void;
  onRowMouseLeave?: (row: Row<typeof kkTableFeatures, TData>) => void;
  columnVisibility?: ColumnVisibilityState;
  columnOrder?: ColumnOrderState;
  initialSorting?: SortingState;
  /**
   * Enables TanStack Virtual row virtualization for large tables.
   * Consumers should provide a bounded height through className for vertical scrolling.
   * Virtualization is applied only above 50 rows to avoid changing small-table behavior.
   */
  virtualization?: boolean;
  testId?: string;
};

type TableVariantProps<TData extends RowData> =
  | {
      variant?: Exclude<TableVariant, 'list'>;
      rowSelection?: never;
    }
  | {
      variant: 'list';
      rowSelection?: TableRowSelection<TData>;
    };

export type TableProps<
  TData extends RowData,
  TDisplayKeys extends string = never,
> = SharedTableProps<TData, TDisplayKeys> & TableVariantProps<TData>;

const minimumRowsForVirtualization = 50;

function getVirtualizedGridTemplateColumns<TData extends RowData>(
  columns: Array<Column<typeof kkTableFeatures, TData, unknown>>,
): string {
  return columns
    .map((column) => {
      const fallbackSize = column.getSize();
      const minSize = column.columnDef.minSize ?? fallbackSize;
      return `minmax(${minSize}px, ${fallbackSize}fr)`;
    })
    .join(' ');
}

function Table<TData extends RowData, TDisplayKeys extends string = never>(
  props: TableProps<TData, TDisplayKeys>,
) {
  const {
    data,
    variant = 'default',
    fixed = false,
    getRowClassName,
    getCellClassName,
    getHeaderClassName,
    stickyHeader = false,
    sortable,
    filterable,
    paginated,
    className,
    onRowClick,
    onRowMouseEnter,
    onRowMouseLeave,
    rowSelection,
    virtualization = false,
  } = props;
  const { setTableContainerRef, tableContainerElement } = useTableContainer();
  const table = useTableInstance(props);

  const shouldVirtualizeRows =
    virtualization && data.length > minimumRowsForVirtualization;
  const virtualizedGridTemplateColumns = shouldVirtualizeRows
    ? getVirtualizedGridTemplateColumns(table.getVisibleLeafColumns())
    : undefined;

  return (
    <>
      <div
        className={cn(
          'relative overflow-auto rounded',
          'laane:border-neutral-10 laane:rounded-md laane:border',
          variant === 'list' &&
            'rounded-none laane:rounded-none laane:border-0',
          className,
        )}
        ref={setTableContainerRef}
        data-virtualized={shouldVirtualizeRows ? 'true' : undefined}
        data-scrolled="false"
        data-scrolled-to-end="false"
      >
        <table
          role={rowSelection ? 'grid' : undefined}
          className={cn(
            'w-full bg-inherit',
            cn(
              {
                'table-relaxed': variant === 'relaxed',
                'table-compact': variant === 'compact',
                'table-list': variant === 'list',
              },
              fixed ? 'table-fixed' : 'table-auto',
            ),
          )}
          style={
            shouldVirtualizeRows
              ? { display: 'grid', width: '100%' }
              : undefined
          }
          data-testid={props.testId}
        >
          {props.caption ? (
            <caption className="sr-only">{props.caption}</caption>
          ) : null}
          <TableHeader<TData>
            data={data}
            filterable={filterable}
            getHeaderClassName={getHeaderClassName}
            gridTemplateColumns={virtualizedGridTemplateColumns}
            isVirtualized={shouldVirtualizeRows}
            sortable={sortable}
            stickyHeader={stickyHeader}
            table={table}
          />
          <TableBody<TData>
            getCellClassName={getCellClassName}
            getRowClassName={getRowClassName}
            gridTemplateColumns={virtualizedGridTemplateColumns}
            isVirtualized={shouldVirtualizeRows}
            onRowClick={onRowClick}
            onRowMouseEnter={onRowMouseEnter}
            onRowMouseLeave={onRowMouseLeave}
            rowSelection={rowSelection}
            table={table}
            tableContainerElement={tableContainerElement}
            variant={variant}
          />
          <TableFooter<TData> table={table} />
        </table>
      </div>
      <TablePagination<TData> paginated={paginated} table={table} />
    </>
  );
}

export default Table;
